import { gql, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { SaveJourneySettingsResponse } from 'modules/journey/components/SettingsTakeover/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { getWorkflow, updateWorkflow } from '../ducks';

const SUCCESS_MESSAGE = 'Conversion settings updated!';
const FAILURE_MESSAGE = `We’re having trouble updating your conversion settings right now. Please try again later.`;

export const disableJourneyConversions = gql`
  mutation disableJourneyConversions($conversionIds: [Long!]!) {
    disableWorkflowGoals(ids: $conversionIds) {
      isActive
    }
  }
`;

type UseDisableJourneyConversions = {
  conversionIds: number[];
  /** Callback when the update was successful */
  onSuccess?: () => void;
  /** Should a success message show? */
  showSuccessToast?: boolean;
  /** Should the workflow be refetched after a successful update? */
  fetchWorkflowOnSuccess?: boolean;
};

type DisableJourneyConversionsResponse = {
  disableWorkflowGoals: {
    isActive: boolean;
  }[];
};

export const useDisableJourneyConversions = () => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const [disableConversions, { loading: isLoading }] =
    useMutation<DisableJourneyConversionsResponse>(disableJourneyConversions);
  const disableJourneyConversionsFn = async ({
    conversionIds,
    onSuccess,
    showSuccessToast = true,
  }: UseDisableJourneyConversions): Promise<SaveJourneySettingsResponse> => {
    try {
      const { data } = await disableConversions({
        variables: {
          conversionIds,
        },
      });
      const allConversionsDisabled = data?.disableWorkflowGoals.length === conversionIds.length;
      if (allConversionsDisabled) {
        if (onSuccess) onSuccess();
        if (showSuccessToast) dispatch(addPositiveToast(SUCCESS_MESSAGE));
        const updatedWorkflow = { ...workflow, goals: [] };
        dispatch(updateWorkflow(updatedWorkflow, { suppressHasUnsavedChangesUpdate: true }));
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useDisableJourneyConversions'));
      }
      return { hasErrors: !allConversionsDisabled };
    } catch {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useDisableJourneyConversions'));
      return { hasErrors: true };
    }
  };
  return { disableJourneyConversions: disableJourneyConversionsFn, isLoading };
};
